/* eslint-disable @typescript-eslint/ban-types */
import type { FlexView, Form } from '@facile-it/shark-morph/components'
import type { FormRow } from '@facile-it/shark-morph/components/Form/FormRow'
import type { FieldDefinition } from '@facile-it/shark-morph/components/Form/fields/FieldDefinitions'
import * as React from 'react'
import type { Subject } from 'rxjs'
import type { ResourceRejected } from '../../types/Resource'
import type { SubType } from '../../types/definition'
import type { TresourceConfig } from '../MasterStateHandler/masterStateBuilder'
import { FormWorker } from './FormWorker'
import type { Unit } from './Unit'
import type { Employe } from './employees/Employe'
import type { EmployeSubscriber } from './employees/EmployeSubscriber'
import type { Validator } from './validators/Validator'
import type { ValidatorMapResult } from './validators/ValidatorResult'

export type Structure<names> = {
    label?: React.ReactNode
    hide?: boolean
    rows: {
        view?: FlexView.Props
        units: {
            name: names
            hide?: boolean
            view?: Unit.ViewType & { flex?: boolean; suffix?: React.ReactNode }
        }[]
    }[]
}[]

interface onRejected<TdataRejected> {
    onRejected: (resources: ResourceRejected<TdataRejected>) => React.ReactNode
}

export type formResource<TdataRejected, TdataFail, canRejected> = {
    state: TresourceConfig<unknown, unknown, TdataRejected, TdataFail, true, canRejected, boolean>
} & (canRejected extends true ? onRejected<TdataRejected> : {})

export type UseType = 'unknown' | 'none' | 'yep'

export interface FormStatus<fnt extends string> {
    name: fnt
    isValid: boolean
}

export type FieldValidator<name> = (
    | {
          validatorResults: ValidatorMapResult
          isValid: 'notValid' | 'valid'
      }
    | {
          isValid: 'unknown'
      }
) & {
    name: name
    isValidating: boolean
}

export type FieldValidatorRunned<name> = Extract<FieldValidator<name>, { isValid: 'notValid' | 'valid' }>

export interface FieldValue<nameType, componentType extends Unit.UnionFields['c']> {
    name: nameType

    value: Unit.pickValueType<componentType>
    initialValue: Unit.pickValueType<componentType>
    data: Unit.pickDateType<componentType>

    use: UseType

    disabled?: boolean
    options?: FieldDefinition.Option[]
}

export type FieldsValue<names extends string = string, c extends Unit.UnionFields['c'] = Unit.UnionFields['c']> = {
    [k in names]: FieldValue<names, c>
}

export interface FieldValueSubmited<
    nameType,
    componentType extends Unit.UnionFields['c'],
    notBlank extends boolean,
    reffinerExluder = notBlank extends true ? undefined : never,
> {
    name: nameType

    value: Exclude<Unit.pickValueType<componentType>, reffinerExluder>
    initialValue: Unit.pickValueType<componentType>
    data: Exclude<Unit.pickDateType<componentType>, reffinerExluder>

    use: UseType
}

export type FieldState<name extends string, c extends Unit.UnionFields['c'] = Unit.UnionFields['c']> = FieldValue<
    name,
    c
> &
    FieldValidatorRunned<name>

export type FieldsState<names extends string, c extends Unit.UnionFields['c'] = Unit.UnionFields['c']> = {
    [k in names]: FieldState<k, c>
}

export type submit = { type: 'none' } | { type: 'std' } | { type: 'custom'; submit: React.ReactNode }

export type updateSubject<componentType extends Unit.UnionFields['c']> = {
    current?: {
        value: Unit.pickValueType<componentType>
        data: Unit.pickDateType<componentType>
        forceOnInt: boolean
    }
    disabled?: boolean
} & (componentType extends 'selectClassic' ? { options?: FieldDefinition.Option[] } : {})

export type employeeStatus = 'init' | 'pending' | 'done' | 'error'

export type helper<name extends string> = { [k in name]?: FormRow.help & { forceSquash?: boolean } }

export function formBuild<
    fnt extends string,
    tags extends string,
    notBlank extends boolean,
    cc extends Unit.UnionFields['c'],
    unitFormTExt extends { componentType: cc; tags: tags[]; notBlank: notBlank },
    //unitsFormT extends { [k: string]: Unit.FormUnit<cc, tags, notBlank> },
    unitsFormT extends { [k: string]: unitFormTExt },
    unitsHtmlT extends { [k: string]: Unit.HtmlUnit },
    employeesFieldsRequest,
    next,
    error,
    employeesT extends {
        [k: string]: Employe.EmployeBuilded<
            SubType<stateFields, employeesFieldsRequest>,
            keyof unitsFormT & string,
            next,
            error
        >
    },
    validtorsFieldsRequest,
    validatorT extends {
        [k: string]: Validator.validatorMultipleBuilded<
            SubType<
                {
                    [k in keyof unitsFormT]: FieldValue<k, unitsFormT[k]['componentType']>
                },
                validtorsFieldsRequest
            >,
            keyof unitsFormT & string
        >
    },
    TdataRejected,
    TdataFail,
    canRejected extends boolean,
    stateFieldsValue = {
        [k in keyof unitsFormT]: FieldValue<k, unitsFormT[k]['componentType']>
    },
    stateFields = {
        [k in keyof unitsFormT]: FieldValue<k, unitsFormT[k]['componentType']> & FieldValidatorRunned<k>
    },
    stateFieldsSubmitted = {
        [k in keyof unitsFormT]: FieldValueSubmited<k, unitsFormT[k]['componentType'], unitsFormT[k]['notBlank']> &
            FieldValidatorRunned<k>
    },
    updaterSubject = Subject<{
        runType: EmployeSubscriber.runType
        fields: { [k in keyof unitsFormT]?: updateSubject<unitsFormT[k]['componentType']> }
    }>,
>(config: {
    name: fnt
    formStyle?: Form.StyleProps
    unitsForm: unitsFormT
    unitsHtml: (fields: stateFields) => unitsHtmlT
    structure: (
        es: { [n in keyof employeesT]: employeeStatus },
        fields: stateFields
    ) => Structure<(keyof unitsFormT | keyof unitsHtmlT) & string>
    onSubmit?: (form: FormStatus<fnt>, fields: stateFieldsSubmitted) => void
    /**
     * `onChange`
     * Trigger a callback on any change happening in the form
     */
    onChange?: (
        form: FormStatus<fnt>,
        fields: stateFields,
        name: keyof unitsFormT,
        units: unitsFormT,
        subject: updaterSubject
    ) => void
    resetOnChange?: { observed: keyof unitsFormT & string; list: Array<keyof unitsFormT & string> }[]
    employees?: {
        config: employeesT
        slave: {
            [k in keyof employeesT]: {
                onNext: (
                    runType: EmployeSubscriber.runType,
                    n: employeesT[k]['next'],
                    s: updaterSubject,
                    u: unitsFormT,
                    f: stateFields
                ) => void
                onError?: (
                    runType: EmployeSubscriber.runType,
                    error: employeesT[k]['error'],
                    s: updaterSubject,
                    u: unitsFormT,
                    f: stateFields
                ) => void
            }
        }
    }
    helper?: (es: { [n in keyof employeesT]: employeeStatus }, fields: stateFields) => helper<keyof unitsFormT & string>
    validators?: validatorT
    fieldsUseMarker?: (
        fields: stateFieldsValue,
        initialized: { [k in keyof unitsFormT]: UseType },
        es: { [n in keyof employeesT]: employeeStatus }
    ) => typeof initialized
    /**
     * `tagsUseMarker`
     * Allow to control grouped fields under a tag with a callback
     */
    tagsUseMarker?: (
        fields: stateFieldsValue,
        initialized: { [k in unitsFormT[keyof unitsFormT]['tags'][0]]: UseType },
        es: { [n in keyof employeesT]: employeeStatus }
    ) => typeof initialized
    submit: submit
    resource?: formResource<TdataRejected, TdataFail, canRejected>
}): React.ReactNode
export function formBuild(config: FormWorker.Props): React.ReactNode {
    // Ho-ho-ho, saw lightning
    return <FormWorker {...config} />
}

export const predicateToUseType = (predicate: boolean): UseType => (predicate ? 'yep' : 'none')
